import { type ReactElement, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import { BillingDniFallback } from '@/modules/perseo-billing/components';
import { AuthenticationForm } from '@/modules/perseo-billing/components/authentication-form/authentication-form';
import { BillingContent } from '@/modules/perseo-billing/containers/billing/Content';
import { BillingModals } from '@/modules/perseo-billing/containers/billing/Modals';
import useBeforeUnload from '@/modules/perseo-billing/hooks/use-before-unload';
import { UseBillingGuard } from '@/modules/perseo-billing/hooks/use-billing-guard';
import useConfirmOrderIntent from '@/modules/perseo-billing/hooks/use-confirm-order-intent';
import useFingerPrint from '@/modules/perseo-billing/hooks/use-fingerprint';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks/use-gtm-billing';
import useViewportMeta from '@/modules/perseo-billing/hooks/use-viewport-meta';
import { UseWebpayTokenHandler } from '@/modules/perseo-billing/hooks/use-webpay-token-handler';
import { setCatMethodEnabled } from '@/modules/perseo-billing/store/slices/billing';
import { getDocumentType } from '@/modules/perseo-billing/store/user/user.reducer';
import { useBrazeCheckoutStarted } from '@/modules/perseo-billing/useBrazeCheckoutStarted.ts';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';

import '@/modules/perseo-billing/index.css';
import '@/modules/perseo-billing/style.scss';
import '@/modules/perseo-billing/style.module.scss';

export const Billing = (): ReactElement => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const customerId = cart?.customerId ?? '';
  const anonymousId = cart?.anonymousId ?? '';

  const { billingPageGtm } = useGtmBillingProduct();
  const dispatch = useAppDispatch();

  // Check for DNI restriction in wedding flow
  const userDocumentType = getDocumentType();
  const guestDocumentType = cart?.shippingAddress?.documentType;
  const documentType = userDocumentType || guestDocumentType;
  const isWedding = isWeddingFlow();
  const shouldShowDniFallback =
    isWedding && documentType === IdentificationDocumentTypes.DNI;

  useBeforeUnload(); // * used to prevent user from leaving the page
  useViewportMeta(); // * used to prevent user from zooming in/out
  useFingerPrint(customerId || anonymousId); // * use to set the fingerprint
  UseWebpayTokenHandler();
  useConfirmOrderIntent();
  UseBillingGuard();
  useBrazeCheckoutStarted();

  useEffect(() => {
    document.body.style.overflow = 'auto';
  }, []);

  useEffect(() => {
    billingPageGtm();

    if (window?.perseoData?.switches?.perseoGlobal?.isBilling3DsEnabled) {
      dispatch(setCatMethodEnabled());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shouldShowDniFallback) {
    return (
      <BillingDniFallback message="Lo sentimos, la compra de regalos para Novios Paris no está habilitada para cuentas registradas con DNI." />
    );
  }

  return (
    <div id="perseo" className="co-flex co-justify-center">
      <div className="co-flex co-flex-col co-overflow-hidden co-mx-0 co-max-w-[1536px] co-w-screen">
        <BillingContent />
        <BillingModals />
        <AuthenticationForm />
      </div>
    </div>
  );
};

export default Billing;
